import React, { useState, useCallback, useRef, useEffect } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useGuideStore } from "../../../store/guideStore";
import { usePenpal } from "../../../hooks/usePenpal";
import { usePublishGuide } from "../../../hooks/usePublishGuide";
import { useUIStore } from "../../../store/uiStore";
import { useNavigateWithLock } from "../../../hooks/useNavigateWithLock";
import { Button, Toggle, Input, Modal } from "../../ui";
import { Copy, ExternalLink, Trash2 } from "lucide-react";
import ResyncGuideButton from "./ResyncGuideButton";
import { debounce } from "lodash";
import { generate } from "random-words";
import PublishingProgress from "./PublishingProgress";
import UpsellModal from "../../modals/UpsellModal";
import * as Sentry from "@sentry/react";
import { getGuidePublicUrl, getGuidesBaseUrl } from "@/lib/utils";
const GuideSettings: React.FC = () => {
  const { t } = useTranslation();
  const { currentGuide, updateGuideSettings, deleteGuide } = useGuideStore();
  const { openExternalUrl } = usePenpal();
  const { openModal, closeModal, setIsSaving, modals } = useUIStore();
  const navigate = useNavigateWithLock();

  const [isDeleting, setIsDeleting] = useState(false);
  const [localGuide, setLocalGuide] = useState(currentGuide);
  const localGuideRef = useRef(localGuide);
  const debouncedSyncRef = useRef<ReturnType<typeof debounce> | null>(null);

  // Keep ref in sync with state
  useEffect(() => {
    localGuideRef.current = localGuide;
  }, [localGuide]);

  const { handlePublish, isPublishing } = usePublishGuide({
    onError: (message) => {
      Sentry.captureException(new Error("Publishing error: " + message));
      toast.error(t("guide.settings.publishingError", { message }), {
        position: "top-center",
        duration: 3000,
      });
    },
    onUpgradeNeeded: () => {
      openModal('upsell');
    },
  });

  const handlePublishClick = (
    event?: React.MouseEvent | Record<string, unknown>
  ) => {
    // Ensure we're not passing the event object to publishGuide
    const extraOptions =
      event && "target" in event
        ? {}
        : (event as Record<string, unknown>) || {};
    handlePublish(extraOptions, localGuide || undefined);
  };

  // Update local state when currentGuide changes
  React.useEffect(() => {
    if (currentGuide) {
      setLocalGuide(currentGuide);
    }
  }, [currentGuide]);


  const generatePassphrase = (numWords = 4, separator = "-") => {
    const words = generate({ exactly: numWords });
    return Array.isArray(words) ? words.join(separator) : words;
  };

  // Create the sync function that always gets the latest localGuide
  const syncSettings = useCallback(async () => {
    const currentLocalGuide = localGuideRef.current;
    if (!currentLocalGuide) return;

    setIsSaving(true);
    try {
      await updateGuideSettings(currentLocalGuide.id, {
        publicUrl: currentLocalGuide.publicUrl,
        isPublished: currentLocalGuide.isPublished,
        publishSettings: currentLocalGuide.publishSettings,
      });
    } catch (error) {
      Sentry.captureException(new Error("Error updating guide settings: " + error));
      toast.error(t("guide.settings.savingError"), {
        position: "top-center",
        duration: 3000,
      });
    } finally {
      setTimeout(() => setIsSaving(false), 400);
    }
  }, [updateGuideSettings, setIsSaving]);

  // Create debounced version once
  useEffect(() => {
    debouncedSyncRef.current = debounce(syncSettings, 300);

    // Cleanup on unmount
    return () => {
      if (debouncedSyncRef.current) {
        debouncedSyncRef.current.cancel();
      }
    };
  }, [syncSettings]);

  const handleSettingChange = (
    updates: Partial<NonNullable<typeof localGuide>>
  ) => {
    if (!localGuide) return;
    const updatedGuide = { ...localGuide, ...updates };
    setLocalGuide(updatedGuide);
    debouncedSyncRef.current?.();
  };

  const handlePublishSettingsChange = (
    updates: Partial<NonNullable<typeof localGuide>["publishSettings"]>
  ) => {
    if (!localGuide) return;

    const updatedGuide = {
      ...localGuide,
      publishSettings: { ...localGuide.publishSettings, ...updates },
    };
    setLocalGuide(updatedGuide);
    debouncedSyncRef.current?.();
  };

  const handleToggleAccessPassword = () => {
    if (!localGuide) return;

    const newAccessPasswordEnabled =
      !localGuide.publishSettings.accessPasswordEnabled;
    const updates: Partial<NonNullable<typeof localGuide>["publishSettings"]> =
      {
        accessPasswordEnabled: newAccessPasswordEnabled,
      };

    if (
      newAccessPasswordEnabled &&
      !localGuide.publishSettings.accessPassword
    ) {
      updates.accessPassword = generatePassphrase();
    }

    handlePublishSettingsChange(updates);
  };

  const handleCopyLink = () => {
    if (!localGuide) return;
    const url = getGuidePublicUrl(localGuide);
    navigator.clipboard.writeText(url);
    toast.success(t("guide.settings.linkCopied"), {
      position: "top-center",
      duration: 3000,
    });
  };

  const handleOpenGuideInBrowser = () => {
    if (!localGuide) return;
    const url = getGuidePublicUrl(localGuide);
    openExternalUrl(url);
  };

  const handleCopyPassword = () => {
    if (!localGuide?.publishSettings.accessPassword) return;
    navigator.clipboard.writeText(localGuide.publishSettings.accessPassword);
    toast.success(t("guide.settings.passwordCopied"), {
      position: "top-center",
      duration: 3000,
    });
  };

  // Delete Guide Functionality

  const handleDeleteGuide = () => {
    openModal('delete');
  };

  const handleConfirmDelete = async () => {
    if (!localGuide) return;
    
    closeModal('delete');
    setIsDeleting(true);
    
    try {
      const result = await deleteGuide(localGuide.id);
      if (result) {
        toast.success(t("guide.settings.guideDeleted"), {
          position: "top-center",
          duration: 3000,
        });
        
        // Reset the guide and switch to add mode
        setLocalGuide(null);
        navigate('/guides');
      } else {
        toast.error(t("guide.settings.deletionError"), {
          position: "top-center",
          duration: 3000,
        });
      }
    } catch (error) {
      console.error('Error deleting guide:', error);
      toast.error(t("guide.settings.deletionError"), {
        position: "top-center",
        duration: 3000,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    closeModal('delete');
  };
  // End of Delete Guide Functionality

  if (!localGuide) {
    return <div>{t("guide.settings.noGuideSelected")}</div>;
  }

  return (
    <div className="bg-white border shadow-lg p-4 px-4 md:p-8 mb-6 container w-[750px] mx-auto">
      {/* Publishing Progress */}
      <PublishingProgress className="lg:p-8" />

      {/* Guide Settings Content */}
      {!isPublishing && (
        <div className="grid gap-4 gap-y-2 text-sm grid-cols-1 md:grid-cols-5">
          {/* Public URL */}
          <div className="md:col-span-5">
            <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
              {t("guide.settings.publicUrl")}
            </label>
            <div className="flex items-center justify-start">
              <div className="slug mr-1 text-gray-700">
                {getGuidesBaseUrl()}
                {localGuide.slug}/
              </div>
              <Input
                type="text"
                value={localGuide.publicUrl}
                onChange={(e) =>
                  handleSettingChange({ publicUrl: e.target.value })
                }
                className="h-8 text-gray-500 text-sm border rounded px-2  mr-5"
              />
              <Button
                variant="secondary"
                size="sm"
                onClick={handleCopyLink}
                className="ml-2 inline-flex items-center justify-center whitespace-nowrap hover:bg-gray-50 font-medium border bg-background shadow-sm h-8 rounded px-3 text-xs transition-colors duration-200 hover:shadow-md"
              >
                <Copy size={16} className="mr-2 text-gray-600" />
                {t("guide.settings.copy")}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={handleOpenGuideInBrowser}
                className="ml-2 inline-flex items-center justify-center whitespace-nowrap hover:bg-gray-50 font-medium border bg-background shadow-sm h-8 rounded px-2 transition-colors duration-200 hover:shadow-md"
              >
                <ExternalLink size={16} className="text-gray-600" />
              </Button>
            </div>
          </div>

          {/* Re-sync Guide */}
          <div className="md:col-span-5">
            <div className="space-y-2 flex flex-row items-center justify-between mt-5">
              <div className="space-y-0.5">
                <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  {t("guide.settings.resyncGuide")}
                </label>
                <p className="text-[0.8rem] text-muted-foreground">
                  {t("guide.settings.resyncDescription")}
                </p>
              </div>
              <ResyncGuideButton
                guideToSync={localGuide}
                onPublishClick={handlePublishClick}
                isPublishing={isPublishing}
              />
            </div>
          </div>

          {/* Delete Guide */}
          <div className="md:col-span-5">
            <div className="space-y-2 flex flex-row items-center justify-between mt-5">
              <div className="space-y-0.5">
                <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  {t("guide.settings.deleteGuideLabel")}
                </label>
                <p className="text-[0.8rem] text-muted-foreground">
                  {t("guide.settings.deleteGuideDescription")}
                </p>
              </div>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleDeleteGuide}
                disabled={isPublishing}
              >
                <Trash2 size={16} className="mr-2" />
                {t("guide.settings.deleteGuideButton")}
              </Button>
            </div>
          </div>

          {/* Visibility */}
          <div className="md:col-span-5">
            <div className="space-y-2 flex flex-row items-center justify-between mt-5">
              <div className="space-y-0.5">
                <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  {t("guide.settings.visibility")}
                </label>
                <p className="text-[0.8rem] text-muted-foreground">
                  {t("guide.settings.visibilityDescription")}
                </p>
              </div>
              <div className="ml-10 flex justify-between items-center">
                <Toggle
                  checked={localGuide.isPublished}
                  onChange={(checked) =>
                    handleSettingChange({ isPublished: checked })
                  }
                />
              </div>
            </div>
          </div>

          {/* Access Password */}
          <div className="md:col-span-5">
            <div className="space-y-2 flex flex-row items-center justify-between mt-5">
              <div className="space-y-0.5">
                <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  {t("guide.settings.passwordAccess")}{" "}
                  <span className="bg-green-100 text-green-800 text-xs font-medium me-2 px-2.5 py-0.5 rounded dark:bg-green-900 dark:text-green-300">
                    {t("guide.settings.new")}
                  </span>
                </label>
                <p className="text-[0.8rem] text-muted-foreground">
                  {t("guide.settings.passwordAccessDescription")}
                </p>
              </div>
              <div className="flex justify-between items-center">
                <Toggle
                  checked={localGuide.publishSettings.accessPasswordEnabled}
                  onChange={handleToggleAccessPassword}
                />
              </div>
            </div>
            {localGuide.publishSettings.accessPasswordEnabled && (
              <div className="flex items-start justify-start gap-2 mt-2">
                <Input
                  type="text"
                  value={localGuide.publishSettings.accessPassword || ""}
                  onChange={(e) =>
                    handlePublishSettingsChange({
                      accessPassword: e.target.value,
                    })
                  }
                  maxLength={60}
                  className="h-8 text-gray-500 text-sm border rounded px-2 !w-[350px]"
                />

                <Button
                  variant="secondary"
                  size="sm"
                  className="ml-2 inline-flex items-center justify-center whitespace-nowrap hover:bg-gray-50 font-medium border bg-background shadow-sm h-8 rounded px-3 text-xs transition-colors duration-200 hover:shadow-md"
                  onClick={handleCopyPassword}
                >
                  <Copy size={16} className="mr-2" />
                  {t("guide.settings.copy")}
                </Button>
              </div>
            )}
          </div>

          {/* Show Author */}
          <div className="md:col-span-5">
            <div className="space-y-2 flex flex-row items-center justify-between mt-5">
              <div className="space-y-0.5">
                <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  {t("guide.settings.showAuthor")}
                </label>
                <p className="text-[0.8rem] text-muted-foreground">
                  {t("guide.settings.showAuthorDescription")}
                </p>
              </div>
              <div className="ml-10 flex justify-between items-center">
                <Toggle
                  checked={localGuide.publishSettings.showAuthor}
                  onChange={(checked) =>
                    handlePublishSettingsChange({ showAuthor: checked })
                  }
                />
              </div>
            </div>
          </div>

          {/* Search Indexing */}
          <div className="md:col-span-5">
            <div className="space-y-2 flex flex-row items-center justify-between mt-5">
              <div className="space-y-0.5">
                <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  {t("guide.settings.searchIndexing")}
                </label>
                <p className="text-[0.8rem] text-muted-foreground">
                  {t("guide.settings.searchIndexingDescription")}
                </p>
              </div>
              <div className="ml-10 flex justify-between items-center">
                <Toggle
                  checked={localGuide.publishSettings.isIndexable}
                  onChange={(checked) =>
                    handlePublishSettingsChange({ isIndexable: checked })
                  }
                />
              </div>
            </div>
          </div>

          {/* Enable Checklist */}
          <div className="md:col-span-5">
            <div className="space-y-2 flex flex-row items-center justify-between mt-5">
              <div className="space-y-0.5">
                <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  {t("guide.settings.enableChecklist")}
                </label>
                <p className="text-[0.8rem] text-muted-foreground">
                  {t("guide.settings.enableChecklistDescription")}
                </p>
              </div>
              <div className="ml-10 flex justify-between items-center">
                <Toggle
                  checked={localGuide.publishSettings.asChecklist}
                  onChange={(checked) =>
                    handlePublishSettingsChange({ asChecklist: checked })
                  }
                />
              </div>
            </div>
          </div>

          {/* Include substeps in TOC */}
          <div className="md:col-span-5">
            <div className="space-y-2 flex flex-row items-center justify-between mt-5">
              <div className="space-y-0.5">
                <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                  {t("guide.settings.includeSubstepsInToc")}
                </label>
                <p className="text-[0.8rem] text-muted-foreground">
                  {t("guide.settings.includeSubstepsDescription")}
                </p>
              </div>
              <div className="ml-10 flex justify-between items-center">
                <Toggle
                  checked={localGuide.publishSettings.showSubstepsInToc}
                  onChange={(checked) =>
                    handlePublishSettingsChange({ showSubstepsInToc: checked })
                  }
                />
              </div>
            </div>
          </div>
        </div>
      )}
         {/* Delete Confirmation Modal */}
       <Modal
        isOpen={modals.delete}
        onClose={handleCancelDelete}
        title=""
        noHeader
        size="md"
      >
        <div className="px-4 my-4 text-black text-sm">
          {t("guide.settings.deleteConfirmation")}
        </div>
        <div className="flex justify-end space-x-3 p-4 border-t border-gray-200">
          <Button
            variant="outline"
            onClick={handleCancelDelete}
            disabled={isDeleting}
            style={{ backgroundColor: '#efefef' }}
          >
            {t("common.cancel")}
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirmDelete}
            disabled={isDeleting}
            loading={isDeleting}
          >
            {t("common.delete")}
          </Button>
        </div>
      </Modal>

      {/* Upsell Modal */}
      <UpsellModal
        isOpen={modals.upsell}
        onClose={() => closeModal('upsell')}
      />
    </div>
  );
};

export default GuideSettings;
